import { useCallback, useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Textarea } from '@/components/atoms/textarea';
import type { EditableLineItem } from '@/components/organisms/OptionCard';
import { lineIdentityKey } from '@/lib/sales/lineIdentity';
import { calcLineSubtotal } from '@/lib/sales/pricingMode';
import { proposalTemplateLineItemToDto } from '@/lib/sales/proposal-template-line-items';
import { useCreateProposalTemplate } from '@/hooks/proposalTemplates/useCreateProposalTemplate';
import { useHasCapability } from '@/hooks/usePermissions';
import type {
  CreateProposalTemplateDto,
  ProposalTemplateOption,
  ProposalTemplate,
} from '@/types/proposalTemplate';
import { TermsSelector } from '@/components/organisms/TermsSelector';
import { TemplateAttachmentsSection } from '@/components/organisms/templates/TemplateAttachmentsSection';
import { TemplateDetailsSection } from '@/components/organisms/templates/TemplateDetailsSection';
import { soleBusinessUnitId } from '@/lib/business-units/businessUnitScope';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { FormPageLayout } from '@/components/layouts';
import { proposalTemplateEndpoints } from '@/lib/api/endpoints';
import { uploadStagedFiles } from '@/lib/sales/uploadStagedFiles';
import { FormAccordionCard, CollapsibleSectionHeader } from '@/components/molecules';
import {
  ProposalTemplateEstimateTemplatePicker,
  ProposalTemplateOptionsSection,
  createProposalTemplateOptionDraft,
  type ProposalTemplateOptionDraft,
} from '@/components/organisms/proposals/ProposalTemplateOptionsSection';
import { mapProposalLineItemToEditable } from '@/routes/_dashboard/dashboard/proposals/_shared';
import { EMPTY_TERMS_SELECTION, toTermsPayload, type TermsSelection } from '@/lib/terms';

export const Route = createFileRoute('/_dashboard/dashboard/settings/templates/proposal/new')({
  component: NewProposalTemplatePage,
});

function NewProposalTemplatePage() {
  const router = useRouter();
  const createMutation = useCreateProposalTemplate();
  const canManageTemplates = useHasCapability('can_manage_templates');

  const [sec, setSec] = useState({
    details: true,
    options: true,
    notes: false,
    attachments: false,
  });
  const toggle = (k: keyof typeof sec) => setSec((p) => ({ ...p, [k]: !p[k] }));

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState<TermsSelection>(EMPTY_TERMS_SELECTION);
  const [options, setOptions] = useState<ProposalTemplateOptionDraft[]>([]);
  // EMPTY = offered to every business unit (a wildcard), not "none".
  const [businessUnitIds, setBusinessUnitIds] = useState<string[]>([]);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);

  const addOption = useCallback(() => {
    setOptions((prev) => [...prev, createProposalTemplateOptionDraft()]);
  }, []);

  const updateOption = useCallback(
    (key: string, updates: Partial<Omit<ProposalTemplateOptionDraft, 'key'>>) => {
      setOptions((prev) => prev.map((o) => (o.key === key ? { ...o, ...updates } : o)));
    },
    [],
  );

  const removeOption = useCallback((key: string) => {
    setOptions((prev) => prev.filter((o) => o.key !== key));
  }, []);

  const importFromTemplate = useCallback(
    (
      optKey: string,
      template: {
        id?: string;
        name: string;
        lineItems?: Record<string, unknown>[];
        estimateTemplateId?: string;
      },
    ) => {
      const rawItems = template.lineItems ?? [];
      const imported = rawItems.map(mapProposalLineItemToEditable);

      const templateId = template.id;
      setOptions((prev) =>
        prev.map((o) => {
          if (o.key !== optKey) return o;
          const existingIds = new Set(o.lineItems.map((li) => li.lineItemId));
          const newItems = imported.filter((li) => !existingIds.has(li.lineItemId));
          return { ...o, lineItems: [...o.lineItems, ...newItems], estimateTemplateId: templateId };
        }),
      );
      toast.success(`Imported ${imported.length} items from "${template.name}"`);
    },
    [],
  );

  const updateLineItem = useCallback(
    // `identity` is `lineIdentityKey(line)`: two configurations of one
    // configurable item share a catalog id, so matching on that edits both rows.
    (optKey: string, identity: string, updates: Partial<EditableLineItem>) => {
      setOptions((prev) =>
        prev.map((o) => {
          if (o.key !== optKey) return o;
          return {
            ...o,
            lineItems: o.lineItems.map((li) => {
              if (lineIdentityKey(li) !== identity) return li;
              const next = { ...li, ...updates };
              if (updates.isOptional === false) next.isSelected = true;
              return next;
            }),
          };
        }),
      );
    },
    [],
  );

  const removeLineItem = useCallback((optKey: string, identity: string) => {
    setOptions((prev) =>
      prev.map((o) => {
        if (o.key !== optKey) return o;
        return { ...o, lineItems: o.lineItems.filter((li) => lineIdentityKey(li) !== identity) };
      }),
    );
  }, []);

  const reorderLineItems = useCallback((optKey: string, oldIndex: number, newIndex: number) => {
    setOptions((prev) =>
      prev.map((o) => {
        if (o.key !== optKey) return o;
        const next = [...o.lineItems];
        const [removed] = next.splice(oldIndex, 1);
        next.splice(newIndex, 0, removed);
        return { ...o, lineItems: next };
      }),
    );
  }, []);

  const replaceLineItems = useCallback((optKey: string, lineItems: EditableLineItem[]) => {
    setOptions((prev) =>
      prev.map((option) => (option.key === optKey ? { ...option, lineItems } : option)),
    );
  }, []);

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (options.length === 0) {
      toast.error('Add at least one option');
      return;
    }
    for (const opt of options) {
      if (!opt.label.trim()) {
        toast.error('Every option needs a label');
        return;
      }
      if (opt.lineItems.length === 0) {
        toast.error(`Option "${opt.label}" needs at least one line item`);
        return;
      }
      if (opt.lineItems.some((li) => !li.code && !li.name.trim())) {
        toast.error(`Name each custom item in "${opt.label}"`);
        return;
      }
    }

    const dto: CreateProposalTemplateDto = {
      name: name.trim(),
      description: description.trim() || undefined,
      notes: notes.trim() || undefined,
      businessUnitIds,
      ...toTermsPayload(terms),
      options: options.map<ProposalTemplateOption>((o) => {
        const subtotal = o.lineItems.reduce((s, li) => s + calcLineSubtotal(li), 0);
        return {
          label: o.label.trim(),
          description: o.description.trim() || undefined,
          color: o.color,
          lineItems: o.lineItems.map(proposalTemplateLineItemToDto),
          subtotal,
          taxTotal: 0,
          total: subtotal,
          estimateTemplateId: o.estimateTemplateId,
        };
      }),
    };

    createMutation.mutate(dto, {
      onSuccess: async (newTemplate: ProposalTemplate) => {
        await uploadStagedFiles(
          stagedFiles,
          proposalTemplateEndpoints.attachmentUploadUrl(newTemplate.id),
        );
        void router.navigate({ to: '/dashboard/settings/templates' });
      },
    });
  };

  if (!canManageTemplates) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center">
        <p className="text-sm text-muted-foreground">
          You do not have permission to create templates.
        </p>
      </div>
    );
  }

  return (
    <FormPageLayout
      title="New Proposal Template"
      onBack={() => router.history.back()}
      noscroll
      actions={
        <>
          <Button variant="outline" size="sm" onClick={() => router.history.back()}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={createMutation.isPending}>
            {createMutation.isPending && <Loader2 size={14} className="mr-1.5 animate-spin" />}
            Create
          </Button>
        </>
      }
    >
      <div className="flex h-full min-h-0">
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 px-4 py-4">
            <FormAccordionCard
              open={sec.details}
              header={
                <CollapsibleSectionHeader
                  label="Details"
                  open={sec.details}
                  onToggle={() => toggle('details')}
                  badge="required"
                />
              }
              bodyClassName="space-y-4"
            >
              <TemplateDetailsSection
                name={name}
                onNameChange={setName}
                placeholder="Standard Proposal"
                businessUnitIds={businessUnitIds}
                onBusinessUnitIdsChange={setBusinessUnitIds}
              />
            </FormAccordionCard>

            <FormAccordionCard
              open={sec.options}
              header={
                <CollapsibleSectionHeader
                  label="Options"
                  open={sec.options}
                  onToggle={() => toggle('options')}
                  badge="required"
                />
              }
              bodyClassName="space-y-4"
            >
              <ProposalTemplateOptionsSection
                options={options}
                onApplySet={(set) => {
                  setOptions(
                    set.map((tier, idx) =>
                      createProposalTemplateOptionDraft({
                        label: tier.label,
                        color: tier.color,
                        collapsed: idx !== 0,
                      }),
                    ),
                  );
                }}
                onAddOption={addOption}
                onUpdateOption={updateOption}
                onRemoveOption={removeOption}
                onUpdateLineItem={updateLineItem}
                onRemoveLineItem={removeLineItem}
                onReorderLineItems={reorderLineItems}
                onReplaceLineItems={replaceLineItems}
                importLabel="Import items from an estimate template"
                importControlFor={(optKey) => (
                  <ProposalTemplateEstimateTemplatePicker
                    onSelect={(template) => importFromTemplate(optKey, template)}
                    businessUnitId={soleBusinessUnitId(businessUnitIds)}
                  />
                )}
              />
            </FormAccordionCard>

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Optional
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <FormAccordionCard
              open={sec.notes}
              header={
                <CollapsibleSectionHeader
                  label="Notes &amp; Terms"
                  open={sec.notes}
                  onToggle={() => toggle('notes')}
                  badge="optional"
                  statusBadge={
                    description.trim() || notes.trim() || terms.termsMode !== 'DEFAULT'
                      ? 'Added'
                      : undefined
                  }
                />
              }
              bodyClassName="space-y-4"
            >
              <div className="space-y-1">
                <Label className="text-xs">Description</Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Good / Better / Best options"
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Internal notes visible on the proposal"
                  rows={2}
                  className="text-sm"
                />
              </div>
              <TermsSelector value={terms} onChange={setTerms} />
            </FormAccordionCard>

            <FormAccordionCard
              open={sec.attachments}
              header={
                <CollapsibleSectionHeader
                  label="Attachments"
                  open={sec.attachments}
                  onToggle={() => toggle('attachments')}
                  badge="optional"
                  statusBadge={stagedFiles.length > 0 ? 'Added' : undefined}
                />
              }
              bodyClassName="space-y-4"
            >
              <TemplateAttachmentsSection
                kind="proposal"
                mode="staging"
                stagedFiles={stagedFiles}
                onStagedFilesChange={setStagedFiles}
                helperText="Files added here are copied to every proposal created from this template."
              />
            </FormAccordionCard>
          </div>
        </div>
      </div>
    </FormPageLayout>
  );
}
